import { Bot } from "lucide-react";
import { useAiRuns } from "@/api/queries";
import type { AiRunFilters, AiRunRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { AiRunLink, ConversationLink, OpportunityLink, OrgLink, StoreLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { CopyIdsButton } from "@/components/tables/copy-ids";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatShortDateTime, formatTime } from "@/lib/dates";
import { formatDuration } from "@/lib/formatters";
import { AI_RUN } from "@/lib/status";

export function AiRunsTable({ query, onChange, toolbar, hide = [], onRowClick, filtered }: ResourceTableProps<AiRunFilters>) {
  const q = useAiRuns(query);
  const columns: Column<AiRunRow>[] = [
    { id: "id", header: "AI run ID", alwaysVisible: true, cell: (r) => <AiRunLink id={r.id} /> },
    { id: "organization", header: "Organization", cell: (r) => <OrgLink org={r.organization} /> },
    { id: "store", header: "Store", cell: (r) => <StoreLink store={r.store} /> },
    { id: "opportunity", header: "Opportunity", cell: (r) => <OpportunityLink id={r.opportunityId} /> },
    { id: "conversation", header: "Conversation", cell: (r) => <ConversationLink id={r.conversationId} /> },
    { id: "model", header: "Model", cell: (r) => <span className="font-mono text-[11px]">{r.model ?? "—"}</span> },
    { id: "prompt", header: "Prompt version", cell: (r) => <span className="font-mono text-[11px]">{r.promptVersion ?? "—"}</span> },
    { id: "trigger", header: "Trigger", defaultHidden: true, cell: (r) => r.trigger.replaceAll("_", " ").toLowerCase() },
    {
      id: "status",
      header: "Status",
      sortKey: "status",
      cell: (r) => (
        <span className="flex items-center gap-1.5">
          <StatusBadge config={AI_RUN[r.status]} />
          {r.errorCode ? <span className="font-mono text-[10.5px] text-danger">{r.errorCode}</span> : null}
        </span>
      ),
    },
    {
      id: "started",
      header: "Started",
      sortKey: "createdAt",
      cell: (r) => (
        <span className="tabular">{r.startedAt ? formatShortDateTime(r.startedAt) : <span className="text-muted-foreground">not started</span>}</span>
      ),
    },
    { id: "completed", header: "Completed", cell: (r) => <span className="tabular">{r.completedAt ? formatTime(r.completedAt) : "—"}</span> },
    { id: "duration", header: "Duration", sortKey: "durationMs", align: "right", cell: (r) => formatDuration(r.durationMs) },
  ];
  return (
    <AdminTable
      id="ai-runs"
      label="AI runs"
      resourceName="AI runs"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(r) => r.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      onRowClick={onRowClick ? (r) => onRowClick(r.id) : undefined}
      selectable
      bulkActions={(rows) => <CopyIdsButton ids={rows.map((r) => r.id)} />}
      toolbar={toolbar}
      empty={
        filtered
          ? { icon: Bot, title: "No AI runs match", description: "Widen the date range or clear filters." }
          : { icon: Bot, title: "No AI runs yet", description: "Runs appear when sellers message and Rogue processes them." }
      }
    />
  );
}
